import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ArrowRightLeft } from 'lucide-react';
import { useRoleSwitching, Role } from '../../hooks/useRoleSwitching';
import { useAppSelector } from '../../store/hooks';
import ConfirmationModal from './ConfirmationModal';

interface RoleSwitcherProps {
  currentRole: Role;
  fullWidth?: boolean;
}

const RoleSwitcher: React.FC<RoleSwitcherProps> = ({ currentRole, fullWidth = false }) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const {
    switchingRole,
    showRoleSwitchConfirm,
    targetRole,
    availableRoles,
    setShowRoleSwitchConfirm,
    setTargetRole,
    handleSwitchRoleClick,
    handleSwitchRole,
  } = useRoleSwitching(currentRole);

  // Get role colors
  const getRoleColors = (role: Role) => {
    switch (role) {
      case 'admin':
        return {
          border: 'border-red-500/30',
          gradient: 'from-red-500/20 to-red-600/20',
          hover: 'hover:from-red-500/30 hover:to-red-600/30',
        };
      case 'host':
        return {
          border: 'border-purple-500/30',
          gradient: 'from-purple-500/20 to-purple-600/20',
          hover: 'hover:from-purple-500/30 hover:to-purple-600/30',
        };
      case 'guest':
        return {
          border: 'border-blue-500/30',
          gradient: 'from-blue-500/20 to-blue-600/20',
          hover: 'hover:from-blue-500/30 hover:to-blue-600/30',
        };
      case 'usher':
        return {
          border: 'border-green-500/30',
          gradient: 'from-green-500/20 to-green-600/20',
          hover: 'hover:from-green-500/30 hover:to-green-600/30',
        };
      default:
        return {
          border: 'border-gray-500/30',
          gradient: 'from-gray-500/20 to-gray-600/20',
          hover: 'hover:from-gray-500/30 hover:to-gray-600/30',
        };
    }
  };

  // Get role label
  const getRoleLabel = (role: Role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'host':
        return 'Host';
      case 'guest':
        return 'Guest';
      case 'usher':
        return 'Usher';
      default:
        return role;
    }
  };

  // Get role description
  const getRoleDescription = (role: Role) => {
    switch (role) {
      case 'admin':
        return 'Manage system and approve events';
      case 'host':
        return 'Create and manage events';
      case 'guest':
        return 'View and respond to invitations';
      case 'usher':
        return 'Manage guest attendance';
      default:
        return 'Switch to this role';
    }
  };

  const currentRoleColors = getRoleColors(currentRole);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Don't show if no available roles
  if (availableRoles.length === 0) {
    return null;
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Current Role Button */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={switchingRole}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
            fullWidth ? 'w-full' : ''
          } ${
            theme === 'dark'
              ? `bg-gradient-to-r ${currentRoleColors.gradient} ${currentRoleColors.border} ${currentRoleColors.hover}`
              : `bg-gradient-to-r ${currentRoleColors.gradient} ${currentRoleColors.border} ${currentRoleColors.hover}`
          }`}
        >
          {/* Role Info */}
          <div className="text-left hidden sm:block">
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Current Role
            </div>
            <div className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {getRoleLabel(currentRole)}
            </div>
          </div>
          
          {/* Mobile: Just show label */}
          <div className="text-left sm:hidden">
            <div className={`text-xs font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {getRoleLabel(currentRole)}
            </div>
          </div>
          
          <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''} ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
        </button>

        {/* Dropdown with Role Cards */}
        {showDropdown && (
          <div className={`absolute ${fullWidth ? 'left-0 w-full' : 'right-0 w-64'} mt-2 rounded-xl shadow-xl border z-50 ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}>
            <div className={fullWidth ? 'p-1.5' : 'p-2'}>
              {availableRoles.map((availableRole) => {
                return (
                  <button
                    key={availableRole.role}
                    onClick={() => {
                      setShowDropdown(false);
                      handleSwitchRoleClick(availableRole.role);
                    }}
                    disabled={switchingRole}
                    className={`w-full flex items-center gap-2 ${fullWidth ? 'p-2' : 'p-3'} rounded-lg transition-all duration-200 ${
                      theme === 'dark'
                        ? 'hover:bg-gray-700/50 text-white'
                        : 'hover:bg-gray-50 text-gray-900'
                    } ${switchingRole ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {/* Role Info */}
                    <div className="flex-1 text-left min-w-0">
                      <div className={`font-semibold ${fullWidth ? 'text-xs' : 'text-sm'} ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {availableRole.label}
                      </div>
                      {!fullWidth && (
                        <div className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {getRoleDescription(availableRole.role)}
                        </div>
                      )}
                    </div>
                    
                    {/* Arrow Icon */}
                    <ArrowRightLeft className={`${fullWidth ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRoleSwitchConfirm}
        onClose={() => {
          setShowRoleSwitchConfirm(false);
          setTargetRole(null);
        }}
        onConfirm={handleSwitchRole}
        title={targetRole ? `Switch to ${availableRoles.find(r => r.role === targetRole)?.label || getRoleLabel(targetRole) + ' Dashboard'}` : 'Switch Dashboard'}
        message={
          targetRole === 'admin'
            ? 'Are you sure you want to switch to the admin dashboard? You will be able to manage the system and approve events.'
            : targetRole === 'guest'
            ? 'Are you sure you want to switch to the guest dashboard? You will be able to view and manage your guest invitations.'
            : targetRole === 'host'
            ? 'Are you sure you want to switch to the host dashboard? You will be able to create and manage your events.'
            : targetRole === 'usher'
            ? 'Are you sure you want to switch to the usher dashboard? You will be able to manage guest attendance for assigned events.'
            : 'Are you sure you want to switch dashboards?'
        }
        confirmText="Switch"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={switchingRole}
      />
    </>
  );
};

export default RoleSwitcher;

